/**
 * UTILITÁRIO PARA COMUNICAÇÃO COM REACT NATIVE WEBVIEW
 * 
 * Este arquivo contém todas as funções necessárias para:
 * 1. Detectar se está rodando dentro de um WebView React Native
 * 2. Enviar mensagens para o React Native
 * 3. Gerenciar downloads e uploads de arquivos
 */

// ===== TIPOS E INTERFACES =====

// Tipos de mensagens que podem ser enviadas ao React Native
export type WebViewMessageType = 
  | 'DOWNLOAD_FILE'     // Solicita download de arquivo
  | 'SELECT_FILE'       // Solicita seleção de arquivo (upload)
  | 'READY';            // Sinaliza que a aplicação web está pronta

// Interface para mensagens enviadas ao React Native
export interface WebViewMessage {
  type: WebViewMessageType;
  payload?: any;
}

// Interface para mensagens recebidas do React Native
export interface WebViewResponse {
  type: 'FILE_SELECTED' | 'DOWNLOAD_COMPLETE' | 'ERROR';
  payload?: any;
}

// ===== FUNÇÕES PRINCIPAIS =====

/**
 * DETECTA SE ESTÁ RODANDO NO REACT NATIVE WEBVIEW
 * 
 * O que faz:
 * - Verifica se existe window.ReactNativeWebView
 * - Retorna true se estiver no React Native, false se estiver no browser
 * 
 * Por que é importante:
 * - Permite que o código se adapte automaticamente
 * - Browser usa input tradicional, React Native usa seletor nativo
 */
export const isReactNativeWebView = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.ReactNativeWebView !== undefined;
  } catch (error) {
    console.error('[Bridge] Erro ao detectar React Native WebView:', error);
    return false;
  }
};

/**
 * ENVIA MENSAGEM PARA O REACT NATIVE
 * 
 * Parâmetros:
 * - message: Objeto com tipo e dados da mensagem
 * 
 * O que faz:
 * - Converte a mensagem para JSON
 * - Envia via window.ReactNativeWebView.postMessage()
 * - Registra no console para debug
 * 
 * Tratamento de erro:
 * - Se não estiver no React Native, apenas avisa no console
 * - Se der erro, registra no console mas não quebra a aplicação
 */
export const sendMessageToRN = (message: WebViewMessage): void => {
  console.log('╔══════════════════════════════════════╗');
  console.log('║  [Bridge] Enviando mensagem para RN  ║');
  console.log('╚══════════════════════════════════════╝');
  console.log('[Bridge] Tipo:', message.type);
  console.log('[Bridge] Payload:', message.payload);
  console.log('[Bridge] isRN:', isReactNativeWebView());
  console.log('[Bridge] window.ReactNativeWebView existe?', !!(window as any).ReactNativeWebView);
  
  if (isReactNativeWebView()) {
    try {
      const jsonMessage = JSON.stringify(message);
      console.log('[Bridge] JSON a enviar:', jsonMessage);
      window.ReactNativeWebView.postMessage(jsonMessage);
      console.log('✅ [Bridge] Mensagem enviada com sucesso!');
    } catch (error) {
      console.error('❌ [Bridge] Erro ao enviar mensagem:', error);
    }
  } else {
    console.warn('⚠️ [Bridge] Não está rodando no React Native WebView');
  }
};

/**
 * SOLICITA DOWNLOAD DE ARQUIVO NO REACT NATIVE
 * 
 * Parâmetros:
 * - fileData: String base64 do arquivo
 * - fileName: Nome do arquivo com extensão (ex: "documento.pdf")
 * - mimeType: Tipo MIME (ex: "application/pdf", "image/jpeg")
 * 
 * O que faz:
 * - Envia mensagem para o React Native solicitando download
 * - O React Native vai salvar o arquivo na pasta Downloads
 * - Funciona apenas no React Native (no browser usa método tradicional)
 */
export const downloadFileRN = (
  fileData: string,
  fileName: string,
  mimeType: string
): void => {
  sendMessageToRN({
    type: 'DOWNLOAD_FILE',
    payload: {
      fileData,      // Base64 string
      fileName,      // Nome do arquivo com extensão
      mimeType,      // Tipo MIME do arquivo
    },
  });
};

/**
 * SOLICITA SELEÇÃO DE ARQUIVO NO REACT NATIVE
 * 
 * Parâmetros:
 * - accept: Tipos de arquivo aceitos (ex: ".pdf", "image/*")
 * - callback: Função que será chamada quando o arquivo for selecionado
 * 
 * O que faz:
 * - Armazena o callback em uma variável global temporária
 * - Envia mensagem para o React Native abrir o seletor de arquivos
 * - Quando o usuário seleciona um arquivo, o React Native envia de volta
 * - O callback é executado com os dados do arquivo
 * 
 * IMPORTANTE: O callback recebe um objeto com:
 * - base64: String base64 do arquivo
 * - fileName: Nome do arquivo
 * - mimeType: Tipo MIME do arquivo
 */
export const selectFileRN = (
  accept: string,
  callback: (result: { base64: string; fileName: string; mimeType: string }) => void
): void => {
  console.log('╔══════════════════════════════════════╗');
  console.log('║  selectFileRN CHAMADO                ║');
  console.log('╚══════════════════════════════════════╝');
  console.log('[selectFileRN] accept:', accept);
  console.log('[selectFileRN] Registrando callback...');
  
  // Armazena o callback para ser chamado quando o arquivo for selecionado
  (window as any).__fileSelectionCallback = callback;
  console.log('✅ [selectFileRN] Callback registrado em window.__fileSelectionCallback');
  
  sendMessageToRN({
    type: 'SELECT_FILE',
    payload: { accept },
  });
};
